import type React from "react"
import { useState, useEffect } from "react"
import { Typography, TextField, Button, Box, Alert, InputAdornment, IconButton, Card, CardContent } from "@mui/material"
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { changePassword } from "../utils/userAPI"
import { STORAGE_KEYS } from "../utils/StorageKeys"

const ChangePassword: React.FC = () => {
  const [username, setUsername] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [newPassword, setNewPassword] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")
  const [error, setError] = useState<string>("")
  const [success, setSuccess] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)

  // Password visibility states
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)

  const navigate = useNavigate()

  useEffect(() => {
  const savedUser = localStorage.getItem(STORAGE_KEYS.USER)
  if (savedUser) {
    try {
      const parsedUser = JSON.parse(savedUser)
      setUsername(parsedUser.id || "")
    } catch (err) {
      console.error("Failed to parse user from localStorage", err)
    }
  }
}, [])

  const validateForm = (): boolean => {
    if (!username.trim()) {
      setError("Username is required")
      return false
    }
    if (!password.trim()) {
      setError("Current password is required")
      return false
    }
    if (!newPassword.trim()) {
      setError("New password is required")
      return false
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match")
      return false
    }
    if (password === newPassword) {
      setError("New password must be different from current password")
      return false
    }
    return true
  }

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
    window.location.reload();
  }

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault()
  setError("")
  setSuccess("")

  if (!validateForm()) return

  setLoading(true)

  const payload = {
    username: username.trim(),
    password,
    newPassword,
    confirmPassword,
  }

  try {
    const message = await changePassword(payload)
    setSuccess(message || "Password changed successfully! Redirecting to login...")

    // Reset fields
    setPassword("")
    setNewPassword("")
    setConfirmPassword("")

    setTimeout(() => {
      handleLogout()
    }, 2000)

  } catch (err: any) {
    setError(err.message || "An unexpected error occurred")
  } finally {
    setLoading(false)
  }
}

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value)
      setError("")
      setSuccess("")
    }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ bgcolor: "background.default", my: 4 }}
    >
      <Card sx={{ maxWidth: 450, width: "100%", mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <img src="/ThoughtDeskLogo.svg" alt="Fleet Management Logo" style={{ height: 90, marginBottom: 10 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              Reset Password
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={username}
              onChange={handleInputChange(setUsername)}
              sx={{ mb: 3 }}
              disabled
              required
            />

            <TextField
              fullWidth
              label="Current Password"
              type={showOldPassword ? "text" : "password"}
              variant="outlined"
              value={password}
              onChange={handleInputChange(setPassword)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle current password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              variant="outlined"
              value={newPassword}
              onChange={handleInputChange(setNewPassword)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              value={confirmPassword}
              onChange={handleInputChange(setConfirmPassword)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              disabled={loading}
              required
            />

            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
              {loading ? "Reseting Password..." : "Reset Password"}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={() => navigate("/")}
              sx={{ mt: 1 }}
              disabled={loading}
              startIcon={<ArrowBack />}
            >
              Back to Home
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default ChangePassword
